import { useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

import { sectionShell } from "./landingContent";
import { useGalleryImages } from "./useGalleryImages";

/**
 * Photo gallery strip: three photos plus an orange "our entrepreneurs in
 * action" tile in a 4-up row.
 *
 * Only the first three images are shown inline; the tile's "See the gallery"
 * link opens the lightbox, which still pages through every active image, so
 * nothing becomes unreachable.
 */
export default function PhotoGallery() {
  const { images, isLoading } = useGalleryImages();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <section className={`${sectionShell} pt-[76px]`}>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[240px] animate-pulse rounded-[22px] bg-white" />
          ))}
        </div>
      </section>
    );
  }

  if (images.length === 0) return null;

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  const goToPrevious = () =>
    setSelectedIndex((i) => (i !== null && i > 0 ? i - 1 : i));
  const goToNext = () =>
    setSelectedIndex((i) => (i !== null && i < images.length - 1 ? i + 1 : i));

  return (
    <section className={`${sectionShell} pt-[76px]`}>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {images.slice(0, 3).map((image, index) => (
          <button
            key={image.id}
            type="button"
            onClick={() => setSelectedIndex(index)}
            className="h-[240px] overflow-hidden rounded-[22px] transition-shadow duration-150 ease-out hover:shadow-eab-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eab-blue-dark focus-visible:ring-offset-2"
          >
            <img
              src={image.resolvedUrl}
              alt={image.caption || "EAB Connect community photo"}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </button>
        ))}

        <button
          type="button"
          onClick={() => setSelectedIndex(0)}
          className="flex h-[240px] flex-col justify-between rounded-[22px] bg-eab-orange p-[30px] text-left text-eab-ink transition-[filter] duration-150 ease-out hover:brightness-[0.93] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eab-ink focus-visible:ring-offset-2"
        >
          <span className="text-pretty text-[28px] font-bold leading-[1.12] tracking-[-0.02em]">
            Our entrepreneurs in action
          </span>
          <span className="text-[16px] font-semibold">See the gallery →</span>
        </button>
      </div>

      {/* Lightbox */}
      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent className="max-w-4xl border-none bg-black/95 p-0">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 z-10 text-white hover:bg-white/20"
              onClick={() => setSelectedIndex(null)}
            >
              <X className="h-6 w-6" />
              <span className="sr-only">Close</span>
            </Button>

            {selectedImage && (
              <div className="flex min-h-[60vh] items-center justify-center">
                <img
                  src={selectedImage.resolvedUrl}
                  alt={selectedImage.caption || "Gallery image"}
                  className="max-h-[80vh] max-w-full object-contain"
                />
              </div>
            )}

            {selectedImage?.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-center text-white">{selectedImage.caption}</p>
              </div>
            )}

            {selectedIndex !== null && selectedIndex > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                onClick={goToPrevious}
              >
                <ChevronLeft className="h-8 w-8" />
                <span className="sr-only">Previous image</span>
              </Button>
            )}

            {selectedIndex !== null && selectedIndex < images.length - 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                onClick={goToNext}
              >
                <ChevronRight className="h-8 w-8" />
                <span className="sr-only">Next image</span>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
